// src/utils/vcard.js
//
// "Save to contacts" — the EAP number is only useful at the moment someone needs
// it, which is rarely the moment they are looking at this app. Handing the phone
// a real vCard puts the provider's line in the place people actually look for a
// number, so the funnel survives leaving the browser.
//
// vCard 3.0 (not 4.0): iOS Contacts, Android Contacts, Outlook and Google
// Contacts all import 3.0 without argument, which is the whole point.

// RFC 2426 §2.4.2 — backslash, comma, semicolon and newline are the four
// characters that change a value's meaning if they arrive raw.
const escapeValue = (value) =>
    String(value)
        .replace(/\\/g, '\\\\')
        .replace(/\r?\n/g, '\\n')
        .replace(/,/g, '\\,')
        .replace(/;/g, '\\;');

// Lines cap at 75 characters; continuations start with a single space. Parsers
// unfold before unescaping, so splitting mid-escape-sequence is safe.
const fold = (line) => (line.length <= 74 ? line : line.match(/.{1,74}/g).join('\r\n '));

/**
 * Build a vCard 3.0 payload.
 * @param {{fullName?:string, org?:string, title?:string, phone?:string,
 *          email?:string, urls?:string[], note?:string}} fields
 */
export function buildVCard(fields = {}) {
    const { fullName, org, title, phone, email, urls = [], note } = fields;
    const display = (fullName || org || 'Contact').trim();

    const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
    // N is required by 3.0. The provider is an organization, not a person, so
    // everything goes in the "family name" slot and FN carries the real label.
    lines.push(`N:${escapeValue(display)};;;;`);
    lines.push(`FN:${escapeValue(display)}`);
    if (org) lines.push(`ORG:${escapeValue(org)}`);
    if (title) lines.push(`TITLE:${escapeValue(title)}`);
    if (phone) lines.push(`TEL;TYPE=WORK,VOICE:${escapeValue(phone)}`);
    if (email) lines.push(`EMAIL;TYPE=INTERNET,WORK:${escapeValue(email)}`);
    urls.filter(Boolean).forEach((url) => lines.push(`URL:${escapeValue(url)}`));
    if (note) lines.push(`NOTE:${escapeValue(note)}`);
    lines.push('END:VCARD');

    return `${lines.map(fold).join('\r\n')}\r\n`;
}

const fileNameFor = (fields) => {
    const base = (fields.fullName || fields.org || 'contact')
        .replace(/[^\w\-. ]+/g, '')
        .trim()
        .replace(/\s+/g, '-');
    return `${base || 'contact'}.vcf`;
};

/**
 * Hand the vCard to the device. Tries the share sheet first — on iOS and Android
 * that offers "Add to Contacts" in one tap, which a file download does not — and
 * falls back to downloading the .vcf everywhere else (desktop, and any browser
 * without file sharing). Must be called from a user gesture.
 *
 * @returns {Promise<'shared'|'downloaded'|'cancelled'>}
 */
export async function saveContact(fields = {}) {
    const vcf = buildVCard(fields);
    const name = fileNameFor(fields);
    const blob = new Blob([vcf], { type: 'text/vcard;charset=utf-8' });

    try {
        if (typeof File === 'function' && navigator.canShare) {
            const file = new File([blob], name, { type: 'text/vcard' });
            if (navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: fields.fullName || fields.org || 'Contact' });
                return 'shared';
            }
        }
    } catch (err) {
        // The user dismissing the share sheet is a decision, not a failure — do
        // not then shove a download at them. Anything else falls through.
        if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return 'cancelled';
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return 'downloaded';
}
